import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, CheckCircle, AlertCircle } from '../icons';
import { fetchCoachRespondentNote, saveCoachRespondentNote } from '../../lib/ai-coach';

interface CoachNotesPanelProps {
  coachId: string;
  respondentId: string;
}

export function CoachNotesPanel({ coachId, respondentId }: CoachNotesPanelProps) {
  const [body, setBody] = useState('');
  const [includeInAiBrief, setIncludeInAiBrief] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const note = await fetchCoachRespondentNote(coachId, respondentId);
        if (cancelled) return;
        if (note) {
          setBody(note.body);
          setIncludeInAiBrief(note.includeInAiBrief);
        } else {
          setBody('');
          setIncludeInAiBrief(true);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [coachId, respondentId]);

  const handleSave = async () => {
    setIsSaving(true);
    setToast(null);
    try {
      await saveCoachRespondentNote(coachId, respondentId, body, includeInAiBrief);
      setToast({ type: 'success', message: 'Notes saved' });
    } catch (err: any) {
      setToast({ type: 'error', message: err?.message || 'Failed to save notes' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="freud-card-elevated p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-cream-100 rounded-xl flex items-center justify-center">
          <FileText className="w-5 h-5 text-chocolate-700" />
        </div>
        <div>
          <h2 className="freud-heading-xl text-chocolate-900">Coach Notes</h2>
          <p className="text-sm text-chocolate-500">
            Private context for this respondent. Optionally fed to your AI Coach clone.
          </p>
        </div>
      </div>

      {toast && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-4 flex items-center gap-2 p-3 rounded-xl ${
            toast.type === 'success' ? 'bg-sage-100 text-sage-700' : 'bg-warm-100 text-warm-700'
          }`}
        >
          {toast.type === 'success' ? (
            <CheckCircle className="w-4 h-4" />
          ) : (
            <AlertCircle className="w-4 h-4" />
          )}
          <p className="text-sm font-medium">{toast.message}</p>
        </motion.div>
      )}

      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Goals, recent themes, sensitivities, preferred tone, key decisions..."
        rows={8}
        disabled={isLoading}
        className="freud-input w-full font-mono text-sm leading-relaxed disabled:opacity-50"
      />

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-chocolate-700 cursor-pointer">
          <input
            type="checkbox"
            checked={includeInAiBrief}
            onChange={(e) => setIncludeInAiBrief(e.target.checked)}
            className="w-4 h-4 rounded border-cream-300"
          />
          Include in AI Coach brief
        </label>

        <button
          onClick={handleSave}
          disabled={isSaving || isLoading}
          className="freud-btn-primary px-6 py-2.5 disabled:opacity-60"
        >
          {isSaving ? 'Saving...' : 'Save Notes'}
        </button>
      </div>

      <p className="mt-3 text-xs text-chocolate-400">
        Visible only to you. When the toggle is on, this text is added to the AI clone's context
        for both chat and voice sessions with this respondent.
      </p>
    </div>
  );
}
